<template>
  <va-modal
    v-model="state.isOpen"
    hide-default-actions
    :title="$t('organizations.deleteModal.title')"
    :size="state.hasEntitlements ? 'large' : 'medium'"
  >
    <div class="delete-organization-modal">
      <!-- Loading state -->
      <div v-if="state.loading" class="loading-section">
        <va-progress-circle indeterminate />
        <p class="mt-3">{{ $t("organizations.deleteModal.checking") }}</p>
      </div>

      <!-- Error state - Organization has entitlements or owners -->
      <div v-else-if="state.hasEntitlements" class="error-section">
        <va-icon name="warning" color="danger" size="large" class="mb-3" />
        <h3 class="mb-3">{{ $t("organizations.deleteModal.cannotDelete") }}</h3>

        <p class="mb-3">
          {{ $t("organizations.deleteModal.hasEntitlements") }}
        </p>

        <!-- Entitlements list -->
        <va-card v-if="state.entitlements.length > 0" class="entitlements-list">
          <va-card-title>
            {{ $t("organizations.deleteModal.associatedEntitlements") }}
            <span
              v-if="state.entitlements.length > 5"
              class="text-sm text-gray-600"
            >
              ({{
                $t("organizations.deleteModal.showingFirst5", {
                  total: state.entitlements.length
                })
              }})
            </span>
          </va-card-title>
          <va-card-content>
            <router-link
              v-for="entitlement in state.entitlements.slice(0, 5)"
              :key="entitlement.id"
              :to="{
                path: `/admin/licensing/entitlements/${entitlement.id}`
              }"
              class="entitlement-item-link"
            >
              <div class="entitlement-item">
                <div class="entitlement-info">
                  <strong>{{
                    entitlement.productName || entitlement.productId
                  }}</strong>
                  <span class="entitlement-id">{{ entitlement.id }}</span>
                </div>
                <va-icon name="arrow_forward" size="small" />
              </div>
            </router-link>
            <div
              v-if="state.entitlements.length > 5"
              class="mt-3 text-sm text-gray-600"
            >
              {{
                $t("organizations.deleteModal.andMore", {
                  count: state.entitlements.length - 5
                })
              }}
            </div>
          </va-card-content>
        </va-card>

        <p class="mt-3">
          {{ $t("organizations.deleteModal.entitlementsInstruction") }}
        </p>
      </div>

      <!-- Success state - Can proceed with deletion -->
      <div v-else class="delete-section">
        <va-icon name="warning" color="warning" size="large" class="mb-3" />
        <h3 class="mb-3">{{ $t("organizations.deleteModal.confirmTitle") }}</h3>
        <p class="mb-3">
          {{
            $t("organizations.deleteModal.confirmMessage", {
              organizationName: organizationName
            })
          }}
        </p>

        <!-- Deletion steps -->
        <va-card class="deletion-steps mt-3">
          <va-card-title>{{
            $t("organizations.deleteModal.deletionSteps")
          }}</va-card-title>
          <va-card-content>
            <div
              class="step"
              v-for="(step, index) in deletionSteps"
              :key="index"
            >
              <va-icon
                :name="step.completed ? 'check' : 'circle'"
                :color="step.completed ? 'success' : 'secondary'"
                class="mr-2"
              />
              <span :class="{ 'completed-step': step.completed }">{{
                step.text
              }}</span>
            </div>
          </va-card-content>
        </va-card>

        <p class="warning-text mt-3">
          {{ $t("organizations.deleteModal.warningMessage") }}
        </p>
      </div>
    </div>

    <template #footer>
      <va-button flat outline class="mr-1" @click="emit('cancel')">
        {{ $t("buttons.cancel") }}
      </va-button>

      <va-button
        v-if="state.hasEntitlements"
        color="primary"
        @click="emit('cancel')"
      >
        {{ $t("buttons.close") }}
      </va-button>

      <va-button
        v-else-if="!state.loading && !state.hasEntitlements"
        color="danger"
        :loading="state.deleting"
        @click="deleteOrganization"
      >
        {{ $t("organizations.deleteModal.confirmDelete") }}
      </va-button>
    </template>
  </va-modal>
</template>

<script setup>
import { computed, reactive, watch, inject } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import {
  callDeleteOrganization,
  callFetchOrganizationMembers
} from "@/api/identity";
import { callFetchEntitlements } from "@/api/licensing";

const { t } = useI18n();
const router = useRouter();
const handleError = inject("handleError");
const toasts = inject("toasts");

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  organizationId: {
    type: String,
    required: true
  },
  organizationName: {
    type: String,
    required: true
  }
});

const state = reactive({
  isOpen: computed(() => props.isOpen),
  loading: false,
  deleting: false,
  hasEntitlements: false,
  entitlements: [],
  members: [],
  memberCount: 0,
  deletionStep: 0
});

const emit = defineEmits(["cancel", "deleted"]);

const deletionSteps = computed(() => [
  {
    text: t("organizations.deleteModal.steps.deleteOrganization"),
    completed: state.deletionStep > 0
  }
]);

/**
 * Check if organization can be deleted (no entitlements)
 */
const checkOrganizationCanBeDeleted = async () => {
  state.loading = true;

  try {
    // First, always fetch members count
    try {
      const membersResponse = await callFetchOrganizationMembers(
        props.organizationId,
        {}
      );

      state.members = membersResponse.data?.items || [];
      state.memberCount = state.members.length;
    } catch (error) {
      console.error("Error fetching organization members:", error);
      throw new Error(`Failed to fetch organization members: ${error.message}`);
    }

    // Then check for entitlements
    try {
      const entitlementsResponse = await callFetchEntitlements({
        params: {
          ownerId: props.organizationId,
          count: 100,
          page: 1
        }
      });

      state.entitlements = entitlementsResponse.data?.items || [];
      state.hasEntitlements = state.entitlements.length > 0;
    } catch (error) {
      console.error("Error fetching entitlements:", error);
      throw new Error(`Failed to fetch entitlements: ${error.message}`);
    }
  } catch (error) {
    console.error("Error checking organization deletion eligibility:", error);
    handleError(error);
  } finally {
    state.loading = false;
  }
};

/**
 * Delete organization
 */
const deleteOrganization = async () => {
  state.deleting = true;

  try {
    await callDeleteOrganization(props.organizationId);
    state.deletionStep = 1;

    toasts.info(t("organizations.deleteModal.successMessage"));
    emit("deleted");

    // Navigate back to organizations list
    router.push("/admin/identity/organizations");
  } catch (error) {
    console.error("Error deleting organization:", error);
    handleError(error);
    toasts.error(t("organizations.deleteModal.errorMessage"));
  } finally {
    state.deleting = false;
  }
};

// Watch for modal opening to trigger checks
watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      state.deletionStep = 0;
      state.hasEntitlements = false;
      state.entitlements = [];
      state.members = [];
      state.memberCount = 0;
      checkOrganizationCanBeDeleted();
    }
  }
);
</script>

<style scoped>
.delete-organization-modal {
  min-width: 400px;
}

.loading-section,
.error-section,
.delete-section {
  text-align: center;
  padding: 1rem 0;
}

.entitlements-list {
  text-align: left;
  margin: 1rem 0;
}

.entitlement-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid var(--va-background-border);
  transition: all 0.2s ease;
}

.entitlement-item:last-child {
  border-bottom: none;
}

.entitlement-info {
  display: flex;
  flex-direction: column;
}

.entitlement-id {
  font-size: 0.8rem;
  color: var(--va-text-color-secondary);
}

.deletion-steps {
  text-align: left;
}

.step {
  display: flex;
  align-items: center;
  margin: 0.5rem 0;
}

.completed-step {
  text-decoration: line-through;
  color: var(--va-text-color-secondary);
}

.warning-text {
  color: var(--va-danger);
  font-weight: bold;
  font-size: 0.9rem;
}

.entitlement-item-link {
  text-decoration: none;
  color: inherit;
  display: block;
  transition: all 0.2s ease;
  border-radius: 6px;
  padding: 4px 8px;
  margin: 2px 0;
}

.entitlement-item-link:hover {
  background-color: var(--va-primary-lighten4, #e3f2fd);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.entitlement-item-link:hover .entitlement-item {
  background-color: transparent;
  border-bottom: none;
}

.entitlement-item-link:hover .va-icon {
  color: var(--va-primary);
  transform: translateX(2px);
}

.entitlement-item-link .va-icon {
  transition: all 0.2s ease;
}
</style>
